import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { Icon } from '../../components/ui/Icon';

import { pushManualScores } from '../../store/slices/tasksSlice';
import { pushToast } from '../../store/slices/uiSlice';
import { usersApi } from '../../services/api';

// ---------------------------------------------------------------------------
// CSV yardımcıları
// ---------------------------------------------------------------------------

/** Serbest metin → entry dizisi. Virgül veya TAB ayrımlı, 1-3 sütun. */
function parseRows(text) {
  const out = [];
  for (const rawLine of (text || '').split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue; // başlık/yorum satırlarını atla
    const parts = line.split(/[\t,]/).map((p) => p.trim());
    const [user_code, rv, pt] = parts;
    const entry = { user_code: (user_code || '').toUpperCase() };
    const rawValue = rv !== undefined && rv !== '' ? Number(rv) : null;
    const points   = pt !== undefined && pt !== '' ? Number(pt) : null;
    if (!entry.user_code) {
      entry._error = 'user_code boş';
    } else if (rawValue !== null && !Number.isFinite(rawValue)) {
      entry._error = `raw_value geçersiz: "${rv}"`;
    } else if (points !== null && !Number.isFinite(points)) {
      entry._error = `points geçersiz: "${pt}"`;
    } else if (rawValue === null && points === null) {
      entry._error = 'raw_value veya points zorunlu';
    } else {
      if (rawValue !== null) entry.raw_value = rawValue;
      if (points   !== null) entry.points    = points;
    }
    out.push(entry);
  }
  return out;
}

function stripInternal(e) {
  // eslint-disable-next-line no-unused-vars
  const { _error, ...rest } = e;
  return rest;
}

/**
 * Kullanıcı listesinden CSV şablonu üret ve indir.
 * BOM eklenir → Excel UTF-8 olarak açar (Türkçe karakterler bozulmaz).
 */
function downloadCsvTemplate(users, taskName) {
  const BOM = '﻿';
  const header = 'user_code,kullanici_adsoyad,kullanici_tipi,raw_value,points';
  const rows = users.map((u) => {
    const code = u.kullanici_kodu || '';
    const name = (u.kullanici_adsoyad || '').replace(/,/g, ' ');
    const tip  = u.kullanici_tipi || '';
    return `${code},${name},${tip},,`;
  });
  const csv  = BOM + [header, ...rows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `manuel_skor_${(taskName || 'gorev').replace(/\s+/g, '_')}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Durum rozeti
// ---------------------------------------------------------------------------
function StatusBadge({ status }) {
  const map = {
    accepted : { color: 'green',   label: 'Yeni'    },
    updated  : { color: 'blue',    label: 'Güncellendi' },
    skipped  : { color: 'yellow',  label: 'Atlandı' },
    error    : { color: 'red',     label: 'Hata'    },
  };
  const cfg = map[status] || { color: 'neutral', label: status };
  return <Badge color={cfg.color} size="sm">{cfg.label}</Badge>;
}

// ---------------------------------------------------------------------------
// Ana bileşen
// ---------------------------------------------------------------------------
export default function ManualScoresDialog({ task, onClose }) {
  const dispatch   = useDispatch();
  const fileRef    = useRef(null);

  // Tab: 'bulk' | 'single'
  const [tab, setTab] = useState('bulk');

  // ── Toplu yükleme state ──
  const [text,       setText]       = useState('');
  const [note,       setNote]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result,     setResult]     = useState(null);

  // ── Kullanıcı listesi (her iki sekme için) ──
  const [users,       setUsers]       = useState([]);   // tüm tenant kullanıcıları
  const [usersStatus, setUsersStatus] = useState('idle'); // idle|loading|error

  // ── Tek kullanıcı state ──
  const [search,       setSearch]       = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [singleRaw,    setSingleRaw]    = useState('');
  const [singlePts,    setSinglePts]    = useState('');
  const [singleResult, setSingleResult] = useState(null);
  const [singleSubmitting, setSingleSubmitting] = useState(false);

  // ── Reset on open ──
  useEffect(() => {
    if (!task) return;
    setText('');  setNote('');  setResult(null);
    setSearch(''); setSelectedUser(null);
    setSingleRaw(''); setSinglePts(''); setSingleResult(null);
    setTab('bulk');
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  // ── Kullanıcıları yükle (tenant-scoped — backend izole ediyor) ──
  const loadUsers = useCallback(async () => {
    setUsersStatus('loading');
    try {
      // Büyük page size: tenant başına genellikle <500 aktif kullanıcı
      const res = await usersApi.list({ size: 500, page: 1 });
      setUsers(res.data || []);
      setUsersStatus('idle');
    } catch {
      setUsersStatus('error');
    }
  }, []);

  // ── CSV template indir ──
  const handleDownloadTemplate = () => {
    if (!users.length) {
      dispatch(pushToast({ kind: 'warning', message: 'Kullanıcı listesi henüz yüklenmedi' }));
      return;
    }
    downloadCsvTemplate(users, task?.name);
  };

  // ── Dosya yükleme ──
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      // BOM varsa kaldır
      const raw = (ev.target.result || '').replace(/^﻿/, '');
      // Başlık satırını atla (user_code ile başlamıyorsa zaten atlanır)
      const lines = raw.split(/\r?\n/).filter(Boolean);
      const dataLines = lines[0]?.toLowerCase().startsWith('user_code')
        ? lines.slice(1)
        : lines;
      setText(dataLines.join('\n'));
    };
    reader.readAsText(file, 'UTF-8');
    // input'u temizle — aynı dosyayı tekrar seçebilsin
    e.target.value = '';
  };

  // ── Toplu gönder ──
  const entries      = useMemo(() => parseRows(text), [text]);
  const parseErrors  = useMemo(() => entries.filter((e) => e._error).length, [entries]);
  const bulkDisabled = submitting || !entries.length || entries.every((e) => e._error);

  const handleBulkSubmit = async () => {
    const valid = entries.filter((e) => !e._error).map(stripInternal);
    if (!valid.length) {
      dispatch(pushToast({ kind: 'error', message: 'Gönderilecek geçerli satır yok' }));
      return;
    }
    setSubmitting(true);
    const res = await dispatch(pushManualScores({ id: task.id, entries: valid, note: note.trim() || null }));
    setSubmitting(false);
    if (res.meta.requestStatus === 'fulfilled') {
      setResult(res.payload);
      const { accepted, updated, errors, skipped } = res.payload || {};
      dispatch(pushToast({
        kind: errors > 0 ? 'warning' : 'success',
        message: `"${task.name}" — yeni: ${accepted || 0}, güncellendi: ${updated || 0}, atlandı: ${skipped || 0}, hata: ${errors || 0}`,
      }));
    } else {
      dispatch(pushToast({ kind: 'error', message: 'Skorlar gönderilemedi' }));
    }
  };

  // ── Tek kullanıcı filtrelenmiş liste ──
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users.slice(0, 50); // ilk 50 göster
    const q = search.trim().toUpperCase();
    return users
      .filter((u) =>
        (u.kullanici_kodu || '').toUpperCase().includes(q) ||
        (u.kullanici_adsoyad || '').toUpperCase().includes(q),
      )
      .slice(0, 50);
  }, [users, search]);

  // ── Tek kullanıcı gönder ──
  const singleDisabled =
    singleSubmitting ||
    !selectedUser ||
    (singleRaw === '' && singlePts === '');

  const handleSingleSubmit = async () => {
    if (!selectedUser) return;
    const entry = { user_code: selectedUser.kullanici_kodu };
    const rv = singleRaw !== '' ? Number(singleRaw) : null;
    const pt = singlePts !== '' ? Number(singlePts) : null;
    if (rv !== null) entry.raw_value = rv;
    if (pt !== null) entry.points    = pt;

    setSingleSubmitting(true);
    const res = await dispatch(pushManualScores({
      id: task.id,
      entries: [entry],
      note: note.trim() || null,
    }));
    setSingleSubmitting(false);
    if (res.meta.requestStatus === 'fulfilled') {
      setSingleResult(res.payload);
      const r = (res.payload?.results || [])[0];
      dispatch(pushToast({
        kind: r?.status === 'error' ? 'warning' : 'success',
        message: r
          ? `${selectedUser.kullanici_kodu} — ${r.status}${r.points != null ? ` (${r.points} puan)` : ''}`
          : 'Skor gönderildi',
      }));
    } else {
      dispatch(pushToast({ kind: 'error', message: 'Skor gönderilemedi' }));
    }
  };

  if (!task) return null;

  return (
    <Modal
      open={Boolean(task)}
      onClose={submitting || singleSubmitting ? () => {} : onClose}
      title={`Manuel Skor — ${task.name}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting || singleSubmitting}>
            Kapat
          </Button>
          {tab === 'bulk' ? (
            <Button loading={submitting} onClick={handleBulkSubmit} disabled={bulkDisabled}>
              {entries.length
                ? `${entries.length - parseErrors} satır gönder`
                : 'Gönder'}
            </Button>
          ) : (
            <Button
              loading={singleSubmitting}
              onClick={handleSingleSubmit}
              disabled={singleDisabled}
            >
              Puan Ata
            </Button>
          )}
        </>
      }
    >
      {/* ── Tab başlıkları ── */}
      <div className="flex border-b border-neutral-200 mb-4">
        {[
          { key: 'bulk',   label: 'Toplu Yükleme',  icon: 'upload'   },
          { key: 'single', label: 'Tek Kullanıcı',  icon: 'user'     },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ' +
              (tab === t.key
                ? 'border-primary-500 text-primary-700'
                : 'border-transparent text-neutral-500 hover:text-neutral-700')
            }
          >
            <Icon name={t.icon} className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* TAB 1 — TOPLU YÜKlEME                                        */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {tab === 'bulk' && (
        <div className="space-y-4">
          {/* Format açıklaması */}
          <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-3 text-xs text-neutral-700">
            <p className="font-medium text-neutral-800 mb-1">Format</p>
            <p>
              Her satır{' '}
              <code className="bg-neutral-200 px-1 rounded">user_code, raw_value, points</code>
              {' '}(virgül veya TAB). <code>points</code> boşsa sunucu tier'dan hesaplar.
              İkisi birden boşsa satır hata ile atılır.
            </p>
            <p className="mt-1 text-neutral-500">
              Örn: <code>ABC123, 42, 50</code> &nbsp;·&nbsp; <code>XYZ789, 18,</code>
            </p>
          </div>

          {/* Şablon indir + dosya yükle */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              icon="download"
              onClick={handleDownloadTemplate}
              disabled={usersStatus === 'loading'}
              title="Tenant kullanıcılarını dolu, raw_value/points sütunlarını boş CSV indir"
            >
              {usersStatus === 'loading' ? 'Yükleniyor…' : 'Excel Şablonu İndir'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon="upload"
              onClick={() => fileRef.current?.click()}
              title="Doldurulmuş CSV / TXT dosyasını yükle"
            >
              Dosya Yükle
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.txt,.tsv"
              className="hidden"
              onChange={handleFileUpload}
            />
            {usersStatus === 'error' && (
              <span className="text-xs text-red-500">Kullanıcılar yüklenemedi</span>
            )}
            {users.length > 0 && (
              <span className="text-xs text-neutral-400">{users.length} tenant kullanıcısı</span>
            )}
          </div>

          {/* Metin alanı */}
          <label className="block">
            <span className="text-sm font-medium text-neutral-700">Satırlar</span>
            <textarea
              value={text}
              onChange={(e) => { setText(e.target.value); setResult(null); }}
              rows={8}
              placeholder={'ABC123, 42, 50\nXYZ789, 18, 20\nDEF456, , 15'}
              className="mt-1 block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </label>

          {entries.length > 0 && (
            <p className="text-xs text-neutral-500">
              {entries.length} satır okundu
              {parseErrors > 0 && (
                <span className="ml-2 text-red-500 font-medium">· {parseErrors} hatalı</span>
              )}
            </p>
          )}

          <Input
            label="Not (opsiyonel)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ör: Nisan 2026 eğitim katılım puanları"
          />

          {/* Sonuç tablosu */}
          {result && <ResultTable result={result} />}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* TAB 2 — TEK KULLANICI                                         */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {tab === 'single' && (
        <div className="space-y-4">
          {!selectedUser ? (
            /* Kullanıcı arama */
            <>
              <div className="relative">
                <Icon
                  name="search"
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none"
                />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Kullanıcı kodu veya ad ara…"
                  className="w-full rounded-md border border-neutral-300 pl-9 pr-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              {usersStatus === 'loading' && (
                <p className="text-sm text-neutral-500 text-center py-4">Kullanıcılar yükleniyor…</p>
              )}
              {usersStatus === 'error' && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
                  <Icon name="alert-triangle" className="h-4 w-4 flex-shrink-0" />
                  Kullanıcı listesi yüklenemedi.
                  <button
                    type="button"
                    className="ml-auto text-xs underline"
                    onClick={loadUsers}
                  >
                    Tekrar dene
                  </button>
                </div>
              )}

              {usersStatus === 'idle' && (
                <>
                  <div className="rounded-md border border-neutral-200 overflow-hidden max-h-72 overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <p className="text-sm text-neutral-400 p-4 text-center">
                        {search ? 'Eşleşen kullanıcı bulunamadı' : 'Kullanıcı yok'}
                      </p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs text-neutral-500 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Kod</th>
                            <th className="px-3 py-2 text-left font-medium">Ad Soyad</th>
                            <th className="px-3 py-2 text-left font-medium">Tip</th>
                            <th className="px-3 py-2 text-left font-medium">Müdürlük</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map((u) => (
                            <tr
                              key={u.kullanici_kodu}
                              onClick={() => {
                                setSelectedUser(u);
                                setSingleRaw('');
                                setSinglePts('');
                                setSingleResult(null);
                              }}
                              className="border-t border-neutral-100 hover:bg-primary-50 cursor-pointer transition-colors"
                            >
                              <td className="px-3 py-2 font-mono text-xs">{u.kullanici_kodu}</td>
                              <td className="px-3 py-2">{u.kullanici_adsoyad || '—'}</td>
                              <td className="px-3 py-2 text-neutral-500 text-xs">{u.kullanici_tipi}</td>
                              <td className="px-3 py-2 text-neutral-500 text-xs">{u.mudurluk || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  {users.length > 50 && !search && (
                    <p className="text-xs text-neutral-400 text-center">
                      İlk 50 kullanıcı gösteriliyor — aramak için yazın
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            /* Seçili kullanıcı — puan giriş formu */
            <div className="space-y-4">
              {/* Kullanıcı kartı */}
              <div className="flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-primary-800">
                    {selectedUser.kullanici_adsoyad || selectedUser.kullanici_kodu}
                  </p>
                  <p className="text-xs text-primary-600 font-mono">
                    {selectedUser.kullanici_kodu}
                    {selectedUser.kullanici_tipi && (
                      <span className="ml-2 font-sans text-primary-500">
                        · {selectedUser.kullanici_tipi}
                      </span>
                    )}
                    {selectedUser.mudurluk && (
                      <span className="ml-2 font-sans text-primary-500">
                        · {selectedUser.mudurluk}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedUser(null); setSearch(''); setSingleResult(null); }}
                  className="text-xs text-primary-500 hover:text-primary-700 underline"
                >
                  Değiştir
                </button>
              </div>

              {/* Puan giriş */}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 space-y-3">
                <p className="text-xs text-neutral-500">
                  <strong>raw_value</strong> girilirse sunucu tier'dan puanı hesaplar.
                  <strong className="ml-1">points</strong> girilirse doğrudan atar.
                  İkisi birden verilirse <strong>points</strong> önceliklidir.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Ham Değer (raw_value)"
                    type="number"
                    value={singleRaw}
                    onChange={(e) => setSingleRaw(e.target.value)}
                    placeholder="ör. 42"
                    hint="Tier eşiğine göre otomatik puan"
                  />
                  <Input
                    label="Doğrudan Puan (points)"
                    type="number"
                    value={singlePts}
                    onChange={(e) => setSinglePts(e.target.value)}
                    placeholder="ör. 15"
                    hint="Tier atlanır, bu puan doğrudan kaydedilir"
                  />
                </div>
                <Input
                  label="Not (opsiyonel)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ör: Nisan eğitim katılımı"
                />
              </div>

              {/* Tek kullanıcı sonucu */}
              {singleResult && (() => {
                const r = (singleResult.results || [])[0];
                if (!r) return null;
                return (
                  <div className={
                    'flex items-center gap-3 rounded-lg border p-3 text-sm ' +
                    (r.status === 'error'
                      ? 'border-red-200 bg-red-50 text-red-800'
                      : 'border-green-200 bg-green-50 text-green-800')
                  }>
                    <Icon
                      name={r.status === 'error' ? 'alert-triangle' : 'check-circle'}
                      className="h-4 w-4 flex-shrink-0"
                    />
                    <div>
                      <StatusBadge status={r.status} />
                      {r.points != null && (
                        <span className="ml-2 font-semibold">{r.points} puan</span>
                      )}
                      {r.reason && (
                        <span className="ml-2 text-xs opacity-75">{r.reason}</span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sonuç tablosu (toplu yükleme sonrası)
// ---------------------------------------------------------------------------
function ResultTable({ result }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white">
      <div className="border-b border-neutral-200 px-3 py-2 text-xs font-medium text-neutral-700 flex gap-4">
        <span className="text-green-700">✓ {result.accepted} yeni</span>
        <span className="text-blue-700">↺ {result.updated} güncellendi</span>
        {result.skipped > 0 && (
          <span className="text-amber-700">⚠ {result.skipped} atlandı</span>
        )}
        {result.errors > 0 && (
          <span className="text-red-700">✗ {result.errors} hata</span>
        )}
      </div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-neutral-50 text-left text-neutral-500 sticky top-0">
            <tr>
              <th className="px-3 py-1.5 font-medium">Kullanıcı</th>
              <th className="px-3 py-1.5 font-medium">Durum</th>
              <th className="px-3 py-1.5 font-medium text-right">Puan</th>
              <th className="px-3 py-1.5 font-medium">Not</th>
            </tr>
          </thead>
          <tbody>
            {(result.results || []).map((r, i) => (
              <tr key={`${r.user_code}-${i}`} className="border-t border-neutral-100">
                <td className="px-3 py-1.5 font-mono">{r.user_code}</td>
                <td className="px-3 py-1.5"><StatusBadge status={r.status} /></td>
                <td className="px-3 py-1.5 text-right font-medium">
                  {r.points != null ? r.points : '—'}
                </td>
                <td className="px-3 py-1.5 text-neutral-500">{r.reason || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
